import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { get } from 'lodash-es';
import Switch from 'react-switch';
import { insiderMethods } from 'site-modules/shared/components/profile/insider-methods';
import { profileScreenMethods } from 'site-modules/shared/components/profile/profile-screen-methods';
import { PriceDropAlertsBanner } from 'site-modules/profile/components/dashboard/price-drop-alerts-banner/price-drop-alerts-banner';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { getSyncDate } from 'site-modules/shared/components/profile/idm/idm-utils';
import { logger } from 'client/utils/logger/logger';
import { getNewAlertsCountStatePath } from 'site-modules/shared/components/profile/price-drop-alerts/alerts-utils';

import './alerts-toggle.scss';

const ALERT_SWITCH_COLOR_PROPS = {
  offColor: '#767676',
  onColor: '#0069BF',
};

const CREATIVE_ID = 'price-drop-alert-message';

const switchIconWrapper = text => <div className="text-white small text-uppercase text-center py-0_25">{text}</div>;

function AlertsToggleUI({
  newAlertsCount = 0,
  isAnonymousUser = null,
  hasNoEmail = true,
  className = '',
  text = 'Price drop emails',
  isEmailSubscribed = false,
  removeDataFromIdm,
  updateIdmData,
  toggleSignModal,
}) {
  const [shouldAlwaysShowAlertBanner, setShouldAlwaysShowAlertBanner] = useState(false);
  const [disabledToggle, setDisabledToggle] = useState(false);

  const trackingValue = useMemo(
    () => `${!isEmailSubscribed || hasNoEmail ? 'enable' : 'disable'} price drop email`,
    [hasNoEmail, isEmailSubscribed]
  );

  async function changeSubscription() {
    setDisabledToggle(true);

    const emailUnsubscribeTs = {
      marketing: {
        globalPreferences: {
          channels: {
            Email: {
              unsubscribedTs: getSyncDate(),
            },
          },
        },
      },
    };

    EventToolbox.fireTrackAction({
      event_type: 'action_completed',
      event_data: {
        action_name: TrackingConstant.ACTION_UPDATE_MEMBERSHIP,
        subaction_name: TrackingConstant.SUBMIT_UNSUBSCRIBE,
        action_cause: 'button_click',
        action_category: 'user',
        creative_id: CREATIVE_ID,
        value: trackingValue,
      },
    });
    try {
      await (isEmailSubscribed ? updateIdmData(emailUnsubscribeTs, true) : removeDataFromIdm(emailUnsubscribeTs));
    } catch (e) {
      logger.error(e);
    }
    setDisabledToggle(false);
  }

  function toggleSign() {
    setShouldAlwaysShowAlertBanner(true);
    EventToolbox.fireTrackAction({
      event_type: 'action_start',
      event_data: {
        action_name: TrackingConstant.ACTION_SIGN_UP,
        subaction_name: TrackingConstant.BEGIN_SIGN_UP,
        action_cause: 'button_click',
        action_category: 'user',
        creative_id: CREATIVE_ID,
        value: trackingValue,
      },
    });
    EventToolbox.fireCustomEvent('begin_sign_up', { value: trackingValue });
    toggleSignModal();
  }

  useEffect(() => {
    EventToolbox.fireTrackAction({
      event_type: 'action_completed',
      event_data: {
        action_name: TrackingConstant.ACTION_SHOW_CONTENT,
        subaction_name: TrackingConstant.PROVIDE_MESSAGE,
        action_cause: 'page_load',
        action_category: 'system',
        creative_id: CREATIVE_ID,
      },
    });
  }, []);

  const showAlertBanner = isAnonymousUser && hasNoEmail;
  const showPriceDropAlertsMessage = !!newAlertsCount && !shouldAlwaysShowAlertBanner;
  const showAlertToggle = showAlertBanner || shouldAlwaysShowAlertBanner;

  return (
    (showPriceDropAlertsMessage || showAlertToggle) && (
      <div className={classnames('alerts-toggle text-gray-darker fw-medium', className)}>
        {showPriceDropAlertsMessage && (
          <div className="d-flex">
            <i className="icon-bell3 mt-md-0 mt-0_25 align-self-md-center text-warning" aria-hidden />
            <div className="ms-0_5">
              <PriceDropAlertsBanner />
            </div>
          </div>
        )}
        {showAlertToggle && (
          <label htmlFor="alert-toggle-switch" className="toggle-switch-label d-inline-flex align-items-center">
            <Switch
              {...ALERT_SWITCH_COLOR_PROPS}
              className="alert-toggle"
              id="alert-toggle-switch"
              onChange={showAlertBanner ? toggleSign : changeSubscription}
              checked={!hasNoEmail ? isEmailSubscribed : false}
              disabled={disabledToggle}
              checkedIcon={switchIconWrapper('on')}
              uncheckedIcon={switchIconWrapper('off')}
              height={24}
              width={60}
            />
            <span className="ps-0_5">{text}</span>
          </label>
        )}
      </div>
    )
  );
}

AlertsToggleUI.propTypes = {
  toggleSignModal: PropTypes.func.isRequired,
  updateIdmData: PropTypes.func.isRequired,
  removeDataFromIdm: PropTypes.func.isRequired,
  isAnonymousUser: PropTypes.bool,
  className: PropTypes.string,
  text: PropTypes.string,
  newAlertsCount: PropTypes.number,
  hasNoEmail: PropTypes.bool,
  isEmailSubscribed: PropTypes.bool,
};

const mapStateToProps = state => ({
  isAnonymousUser: state.profile.isAnonymousUser,
  newAlertsCount: get(state, getNewAlertsCountStatePath()),
  hasNoEmail: !get(state, 'profile.data.idm.identifiers.email[0]'),
  isEmailSubscribed: !get(state, 'profile.data.idm.marketing.globalPreferences.channels.Email.unsubscribedTs', ''),
});

export const AlertsToggle = connect(mapStateToProps)(insiderMethods(profileScreenMethods(AlertsToggleUI)));
